import { useRef } from 'react';

export function useRichTextEditor() {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  const applyFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertList = (ordered: boolean) => {
    applyFormat(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const insertBlockquote = () => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return;

    // Check if we're already in a blockquote
    const existingBlockquote =
      sel.anchorNode.parentElement?.closest('blockquote');

    if (existingBlockquote) {
      // Remove blockquote - move content out
      const parent = existingBlockquote.parentNode;
      while (existingBlockquote.firstChild) {
        parent?.insertBefore(existingBlockquote.firstChild, existingBlockquote);
      }
      existingBlockquote.remove();
    } else {
      // Add blockquote
      applyFormat('formatBlock', 'blockquote');
    }

    editorRef.current?.focus();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const applyLink = (linkUrl: string) => {
    if (!linkUrl.trim()) return;

    // Restore the saved selection
    const sel = window.getSelection();
    if (!sel || !savedSelectionRef.current) return;

    sel.removeAllRanges();
    sel.addRange(savedSelectionRef.current);

    const range = savedSelectionRef.current;
    const selectedText = range.toString().trim();

    if (selectedText) {
      // Apply link to selected text using execCommand
      document.execCommand('createLink', false, linkUrl);
    } else {
      // No selection - insert the URL as both text and link
      const link = document.createElement('a');
      link.href = linkUrl;
      link.textContent = linkUrl;
      link.contentEditable = 'false'; // Make the link not editable

      range.deleteContents();
      range.insertNode(link);

      // Add a space after the link for continued typing
      const space = document.createTextNode('\u00A0');
      link.parentNode?.insertBefore(space, link.nextSibling);

      // Move cursor after the space
      range.setStartAfter(space);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    savedSelectionRef.current = null;
    editorRef.current?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    onSend: () => void,
  ) => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return;

    // Shift+Enter sends message
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      onSend();
      return;
    }

    // Handle `Enter` in blockquote:
    // First `Enter` = new line inside of blockquote;
    // Second `Enter` (on an empty line) = exit.
    if (e.key === 'Enter') {
      const anchorEl =
        sel.anchorNode.nodeType === Node.ELEMENT_NODE
          ? (sel.anchorNode as Element)
          : sel.anchorNode.parentElement;
      const blockquote = anchorEl?.closest('blockquote');
      if (!blockquote) return;

      // Find the direct child of blockquote that contains the cursor.
      let line: Node | null = sel.anchorNode;
      while (line && line.parentNode !== blockquote) {
        line = line.parentNode;
      }

      const textIsEmpty = (node: Node | null) =>
        !node || !(node.textContent ?? '').trim();

      const lastChild = blockquote.lastChild;
      const cursorOnEmptyLastLine =
        !!line && line === lastChild && textIsEmpty(line);
      const trailingBr =
        (!line || line === blockquote) && lastChild?.nodeName === 'BR';
      const blockquoteIsEmpty =
        textIsEmpty(blockquote) && !blockquote.children.length;

      if (!cursorOnEmptyLastLine && !trailingBr && !blockquoteIsEmpty) {
        // First Enter (or Enter on a non-empty line) — insert a <br> so the
        // cursor stays in the same blockquote instead of the browser splitting
        // it into two blocks.
        e.preventDefault();
        document.execCommand('insertLineBreak');
        return;
      }

      e.preventDefault();

      // Strip the trailing empty line/br so it doesn't linger in the blockquote.
      if (cursorOnEmptyLastLine && line && line.parentNode === blockquote) {
        blockquote.removeChild(line);
      } else if (trailingBr && lastChild) {
        blockquote.removeChild(lastChild);
      }

      const newLine = document.createElement('div');
      newLine.appendChild(document.createElement('br'));
      blockquote.parentNode?.insertBefore(newLine, blockquote.nextSibling);

      if (textIsEmpty(blockquote) && !blockquote.children.length) {
        blockquote.remove();
      }

      const newRange = document.createRange();
      newRange.setStart(newLine, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
  };

  const getEditorContent = () => {
    return editorRef.current?.innerText.trim() || '';
  };

  /**
   * The editor's markup, for a field whose stored format is HTML.
   *
   * `getEditorContent()` returns `innerText` and is what every plain-text
   * consumer wants; the study-group announcement is stored as raw HTML (7 of
   * the 17 migrated rows carry tags) and its toolbar — bold, italic,
   * underline, blockquote — is exactly legacy's Trumbowyg button set
   * (`themes/wbs/assets/js/main-script.js:687-693`), so `innerText` would drop
   * every applied format on save.
   */
  const getEditorHtml = () => editorRef.current?.innerHTML ?? '';

  /**
   * Load stored markup into the editor. The hook previously had no setter at
   * all — `clearEditor` only blanks it — so an existing announcement could not
   * be edited. Callers must fire this exactly once per loaded record (a ref
   * latch), or a refetch overwrites what the author is typing.
   */
  const setEditorHtml = (html: string) => {
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
  };

  const clearEditor = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  return {
    editorRef,
    applyFormat,
    insertList,
    insertBlockquote,
    saveSelection,
    applyLink,
    handleKeyDown,
    getEditorContent,
    getEditorHtml,
    setEditorHtml,
    clearEditor,
  };
}
